import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Minus, TrendingDown, TrendingUp, Users } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";

import type { DailyDebtSummary } from "../types";

interface DebtSummaryCardProps {
  summary: DailyDebtSummary | null;
  previousDayDebt: number | null;
  totalClients: number;
  loading?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercentage = (value: number): string => {
  return new Intl.NumberFormat("es-CL", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

export const DebtSummaryCard = ({
  summary,
  previousDayDebt,
  totalClients,
  loading = false,
}: DebtSummaryCardProps) => {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-4 w-24" />
        </CardHeader>
        <CardContent>
          <Skeleton className="mb-2 h-10 w-48" />
          <Skeleton className="mb-4 h-4 w-32" />
          <Skeleton className="h-4 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Deuda Total
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Seleccione una fecha para ver el resumen de deuda
          </p>
        </CardContent>
      </Card>
    );
  }

  const change =
    previousDayDebt !== null ? summary.total_debt - previousDayDebt : null;

  const changePercentage =
    previousDayDebt !== null && previousDayDebt !== 0
      ? ((summary.total_debt - previousDayDebt) / previousDayDebt) * 100
      : null;

  const formattedDate = format(
    new Date(summary.snapshot_date + "T00:00:00"),
    "d 'de' MMMM, yyyy",
    { locale: es }
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Deuda Total
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">
          {formatCurrency(summary.total_debt)}
        </div>

        <p className="mt-1 text-sm text-muted-foreground">Al {formattedDate}</p>

        {change !== null && (
          <div className="mt-3 flex items-center gap-2">
            {change > 0 ? (
              <>
                <TrendingUp className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-500">
                  +{formatCurrency(change)}
                  {changePercentage !== null && (
                    <span className="ml-1">
                      ({formatPercentage(changePercentage)})
                    </span>
                  )}
                </span>
              </>
            ) : change < 0 ? (
              <>
                <TrendingDown className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-500">
                  {formatCurrency(change)}
                  {changePercentage !== null && (
                    <span className="ml-1">
                      ({formatPercentage(changePercentage)})
                    </span>
                  )}
                </span>
              </>
            ) : (
              <>
                <Minus className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Sin cambios desde el día anterior
                </span>
              </>
            )}
            <span className="text-xs text-muted-foreground">
              vs día anterior
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>{totalClients.toLocaleString("es-CL")} clientes con deuda</span>
        </div>
      </CardContent>
    </Card>
  );
};
